import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Checkbox, Divider, Dropdown, Input } from "antd";
import { useEffect, useMemo, useState } from "react";
import { truncateText } from "../../../common/utils/tranculateText";

const MultipleFilter = ({
  field,
  onChange,
  options,
  values,
  autoOpen,
  readOnly = false,
}) => {
  const [search, setSearch] = useState();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
    }
  }, [autoOpen]);

  const updateSearch = (e) => {
    const searchValue = e.target.value;
    if (searchValue && searchValue.trim().length) {
      setSearch(searchValue);
    } else {
      setSearch(null);
    }
  };

  const dropdownOptions = useMemo(() => {
    return search
      ? options.filter((option) => {
          const name = option.name || `${option.firstName} ${option.lastName}`;
          return name.toLowerCase().includes(search.toLowerCase());
        })
      : options;
  }, [search, options]);

  const allOptionsMap = useMemo(() => {
    return options.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [options]);

  const getDropdownSearch = (name, id) => ({
    key: id,
    label: (
      <div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <SearchOutlined style={{ color: "#6C6C89" }} />
          <Input
            variant="borderless"
            value={search}
            placeholder={name}
            onChange={updateSearch}
            maxLength={255}
          />
        </div>
        <Divider style={{ margin: 0 }} />
      </div>
    ),
    disabled: true,
  });

  const getDropdownItem = (name, id) => ({
    key: id,
    label: (
      <div
        key={id}
        style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          cursor: readOnly ? "default" : "pointer",
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (readOnly) return;
          onChange(field.id, id);
          setOpen(true);
        }}
        role="presentation"
        data-testid={`filter-${field.id}-option-${id}`}
      >
        <Checkbox checked={values.includes(id)} />
        <span style={{ marginLeft: 10 }}>{truncateText(name, 55)}</span>
      </div>
    ),
  });

  const filters = () => {
    const content = [];

    content.push(getDropdownSearch("Search", "dropdown-search"));

    dropdownOptions.forEach((item) =>
      content.push(
        getDropdownItem(
          item.name || `${item.firstName} ${item.lastName}`,
          item.id
        )
      )
    );

    return content;
  };

  const generateTagString = () => {
    if (!values.length) return "";

    let string = "";

    for (let i = 0; i < values.length && i < 3; i += 1) {
      const selected = allOptionsMap[values[i]] || {};
      const label =
        selected.name || `${selected.firstName || ""} ${selected.lastName || ""}`.trim();

      if (i !== 2) {
        string = i ? `${string}, ${label}` : label;
      } else {
        string = `${string}, ${values.length - 2} more`;
      }
    }

    return string;
  };

  return (
    <Dropdown
      menu={{
        items: filters(),
        onClick: (e) => {
          e.domEvent?.preventDefault?.();
          e.domEvent?.stopPropagation?.();
        },
      }}
      styles={{ root: {
        maxHeight: 320,
        overflow: "auto",
        boxShadow: "0px 20px 140px 0px rgba(0, 0, 0, 0.15)",
      } }}
      trigger={["click"]}
      open={open}
      onOpenChange={(visible) => setOpen(visible)}
    >
      <div
        onClick={() => setOpen(true)}
        role="presentation"
        data-testid={`filter-${field.id}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: "#FFFFFF",
          border: "1px solid #6C6C8929",
          borderRadius: 8,
          padding: "5px 10px",
          fontSize: 14,
          cursor: "pointer",
          maxWidth: "100%",
        }}
      >
        <span style={{ fontWeight: 600 }}>{field.name}: </span>

        <span
          style={{
            color: "rgba(108, 108, 137, 1)",
            marginLeft: 4,
            whiteSpace: "nowrap",
          }}
        >
          {truncateText(generateTagString(), 30)}
        </span>

        {!readOnly && (
          <>
            <div
              style={{
                width: 1,
                height: 16,
                background: "#6C6C89",
                opacity: 0.3,
                borderRadius: 1,
                marginLeft: 8,
                marginRight: 8,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                cursor: "pointer",
                color: "rgba(178, 178, 199, 1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(field.id);
              }}
              role="presentation"
            >
              <CloseOutlined style={{ fontSize: 12 }} />
            </span>
          </>
        )}
      </div>
    </Dropdown>
  );
};

export default MultipleFilter;
